import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  trainingService,
  Instance,
  InstanceStatus,
  GpuType,
  PricingRow,
} from '../services/trainingApi';
import { walletApi } from '../services/platformApi';
import { formatGpuName } from '../lib/format';
import { LaunchPrefill } from './Gpus';
import {
  Page,
  PageHeader,
  ConsoleTabs,
  StatTile,
  SectionHeader,
  Tag,
  Notice,
  TableCard,
  formatINR,
} from '../components/console/ui';
import InstanceStatusTag from '../components/console/InstanceStatusTag';
import { ServersIc, TrashIc, WarnIc } from '../components/console/icons';

function serverMsg(e: any, fallback: string): string {
  return e?.response?.data?.message || e?.response?.data?.error || fallback;
}

// Statuses that are still settling - while any instance is in one of these we
// keep polling the list (mirrors Instances.tsx).
const TRANSITIONAL: InstanceStatus[] = ['allocating', 'starting', 'stopping'];
const ACTIVE: InstanceStatus[] = ['running', 'allocating', 'starting', 'stopping'];

const vramLabel = (gt?: GpuType) => (gt?.vram_gb ? `${gt.vram_gb} GB` : '-');

function TerminateButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      className="ps-btn-danger ps-btn-sm !px-2.5"
      disabled={disabled}
      onClick={onClick}
      aria-label="Terminate instance"
      title="Terminate"
    >
      <TrashIc className="w-4 h-4" />
    </button>
  );
}

export default function Dashboard() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const walletQ = useQuery({
    queryKey: ['wallet'],
    queryFn: () => walletApi.getWallet().then((r) => r.data),
  });

  const instancesQ = useQuery({
    queryKey: ['instances'],
    queryFn: () => trainingService.listInstances().then((r) => r.data.data ?? []),
    refetchInterval: (q) => {
      const list = (q.state.data as Instance[] | undefined) ?? [];
      return list.some((i) => TRANSITIONAL.includes(i.status)) ? 4000 : false;
    },
  });

  const typesQ = useQuery({
    queryKey: ['gpu-types'],
    queryFn: () => trainingService.getGpuTypes().then((r) => r.data.data ?? []),
  });
  const pricingQ = useQuery({
    queryKey: ['pricing'],
    queryFn: () => trainingService.getPricing().then((r) => r.data.data ?? []),
  });

  const instances: Instance[] = instancesQ.data ?? [];
  const active = instances.filter((i) => ACTIVE.includes(i.status));
  const inactive = instances.filter((i) => !ACTIVE.includes(i.status));

  const types: GpuType[] = useMemo(() => typesQ.data ?? [], [typesQ.data]);
  const pricing: PricingRow[] = useMemo(() => pricingQ.data ?? [], [pricingQ.data]);
  const vramByType = useMemo(
    () => new Map<string, GpuType>(types.map((t) => [t.gpu_type, t])),
    [types]
  );

  const balance = Number(
    walletQ.data?.available_balance ?? walletQ.data?.balance ?? 0
  );

  // Show available GPUs first; cap the list so the dashboard stays compact.
  const availableGpus = useMemo(
    () =>
      [...pricing].sort((a, b) => Number(b.available) - Number(a.available)).slice(0, 6),
    [pricing]
  );

  const availableGpuCount = useMemo(
    () => pricing.filter((p) => p.available).length,
    [pricing]
  );

  // Lowest-priced available offer, from the same pricing data.
  const cheapest = useMemo(
    () =>
      pricing
        .filter((p) => p.available)
        .reduce<PricingRow | null>((best, p) => (!best || p.price_per_hour < best.price_per_hour ? p : best), null),
    [pricing]
  );

  const deleteM = useMutation({
    mutationFn: (id: string) => trainingService.deleteInstance(id),
    onSuccess: () => {
      toast.success('Termination requested');
      qc.invalidateQueries({ queryKey: ['instances'] });
    },
    onError: (e: any) => toast.error(serverMsg(e, 'Could not terminate instance')),
  });

  const launch = (p: PricingRow) => {
    const prefill: LaunchPrefill = { gpu_type: p.gpu_type, region: p.region, tier: p.tier };
    navigate('/console/instances', { state: { prefill } });
  };

  const terminate = (inst: Instance) => {
    if (
      window.confirm(
        `Terminate "${inst.name || inst.id}"? This permanently destroys the instance.`
      )
    ) {
      deleteM.mutate(inst.id);
    }
  };

  const catalogLoading = pricingQ.isLoading || typesQ.isLoading;

  return (
    <Page>
      <PageHeader
        title="Dashboard"
        subtitle="Rent NVIDIA GPUs by the hour for training and research. Connect from your terminal with the podstack CLI."
        actions={
          <Link to="/console/gpus" className="ps-btn">
            Rent a GPU
          </Link>
        }
      />
      <ConsoleTabs />

      {/* Stats row */}
      <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(min(100%,200px),1fr))]">
        <StatTile label="Active instances" value={instancesQ.isLoading ? '-' : active.length} />
        <StatTile label="GPU offers available" value={catalogLoading ? '-' : availableGpuCount} />
        <StatTile
          label="Cheapest now"
          value={
            cheapest ? (
              <>
                {formatINR(cheapest.price_per_hour)}
                <span className="text-sm text-ps-subtle">/hr</span>
              </>
            ) : (
              '-'
            )
          }
          sub={cheapest ? `${formatGpuName(cheapest.gpu_type)} · ${cheapest.region}` : undefined}
        />
        <StatTile
          label="Wallet balance"
          value={walletQ.isLoading || walletQ.isError ? '-' : formatINR(balance)}
        />
      </div>

      {/* Running instances */}
      <section className="flex flex-col gap-3" aria-labelledby="dash-running">
        <SectionHeader
          id="dash-running"
          title="Running instances"
          action={
            <Link to="/console/instances" className="ps-link">
              All instances →
            </Link>
          }
        />

        {instancesQ.isLoading ? (
          <Notice>Loading instances…</Notice>
        ) : instancesQ.isError ? (
          <Notice tone="error">Could not load instances.</Notice>
        ) : active.length === 0 ? (
          <div className="ps-card p-5 flex flex-wrap items-center gap-4">
            <span className="ps-num w-10 h-10">
              <ServersIc />
            </span>
            <p className="m-0 flex-1 basis-[260px] text-[13px] leading-relaxed text-ps-muted">
              No running instances, so nothing is billing.{' '}
              <Link to="/console/gpus" className="ps-link">
                Rent a GPU
              </Link>{' '}
              to get started.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <TableCard className="hidden md:block">
              <thead>
                <tr>
                  <th>Instance</th>
                  <th>GPU</th>
                  <th>Status</th>
                  <th>Region</th>
                  <th className="!text-right">Cost / hr</th>
                  <th>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {active.map((inst) => (
                  <tr key={inst.id}>
                    <td className="!whitespace-normal">
                      <div className="font-medium text-ps-text">{inst.name || inst.id}</div>
                      <code className="font-mono text-[11px] text-ps-subtle">{inst.id}</code>
                      {inst.billing_warning && (
                        <div className="mt-2 flex items-start gap-2 rounded-md bg-ps-warn-bg px-2 py-1.5 text-xs text-ps-warn">
                          <WarnIc className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span>{inst.billing_warning}</span>
                        </div>
                      )}
                    </td>
                    <td>
                      {inst.gpu_count}× {formatGpuName(inst.gpu_type)}
                    </td>
                    <td>
                      <InstanceStatusTag status={inst.status} />
                    </td>
                    <td className="text-ps-muted">{inst.region}</td>
                    <td className="text-right font-medium">{formatINR(inst.price_per_hour)}</td>
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        {inst.status === 'running' && (
                          <Link
                            to={`/console/connect?id=${encodeURIComponent(inst.id)}`}
                            className="ps-btn2 ps-btn-sm"
                          >
                            Connect
                          </Link>
                        )}
                        <TerminateButton disabled={deleteM.isPending} onClick={() => terminate(inst)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </TableCard>

            {/* Mobile cards */}
            <div className="flex flex-col gap-3 md:hidden">
              {active.map((inst) => (
                <div key={inst.id} className="ps-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-ps-text">{inst.name || inst.id}</span>
                        <InstanceStatusTag status={inst.status} />
                      </div>
                      <code className="block truncate font-mono text-[11px] text-ps-subtle">{inst.id}</code>
                    </div>
                    <div className="flex items-center gap-2">
                      {inst.status === 'running' && (
                        <Link
                          to={`/console/connect?id=${encodeURIComponent(inst.id)}`}
                          className="ps-btn2 ps-btn-sm"
                        >
                          Connect
                        </Link>
                      )}
                      <TerminateButton disabled={deleteM.isPending} onClick={() => terminate(inst)} />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-ps-raised pt-3 text-xs text-ps-muted">
                    <span>
                      {inst.gpu_count}× {formatGpuName(inst.gpu_type)}
                    </span>
                    <span>{inst.region}</span>
                    <span>{inst.tier}</span>
                    <span>{formatINR(inst.price_per_hour)}/hr</span>
                  </div>
                  {inst.billing_warning && (
                    <div className="mt-3 flex items-start gap-2 rounded-md bg-ps-warn-bg px-3 py-2 text-xs text-ps-warn">
                      <WarnIc className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{inst.billing_warning}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* Stopped / terminated instances */}
      {inactive.length > 0 && (
        <section className="flex flex-col gap-2.5" aria-labelledby="dash-inactive">
          <h2 id="dash-inactive" className="m-0 text-[15px] font-medium text-ps-muted">
            Stopped &amp; terminated
          </h2>
          <div className="ps-card divide-y divide-ps-raised">
            {inactive.map((inst) => (
              <div key={inst.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="truncate text-[13px] text-ps-text">{inst.name || inst.id}</span>
                  <InstanceStatusTag status={inst.status} />
                </div>
                <div className="flex items-center gap-3 text-xs text-ps-subtle">
                  <span>
                    {inst.gpu_count}× {formatGpuName(inst.gpu_type)}
                  </span>
                  <span>{inst.region}</span>
                  <button
                    type="button"
                    className="grid h-8 w-8 place-items-center rounded-md text-ps-subtle hover:bg-ps-err-bg hover:text-ps-err disabled:opacity-40 disabled:hover:bg-transparent"
                    disabled={inst.status === 'terminated' || deleteM.isPending}
                    onClick={() => terminate(inst)}
                    aria-label="Terminate instance"
                  >
                    <TrashIc className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Available GPUs */}
      <section className="flex flex-col gap-3" aria-labelledby="dash-gpus">
        <SectionHeader
          id="dash-gpus"
          title="Available GPUs"
          action={
            <Link to="/console/gpus" className="ps-link">
              Browse catalog →
            </Link>
          }
        />

        {catalogLoading ? (
          <Notice>Loading GPU catalog…</Notice>
        ) : pricingQ.isError || typesQ.isError ? (
          <Notice tone="error">Could not load the GPU catalog.</Notice>
        ) : availableGpus.length === 0 ? (
          <Notice>No GPUs available right now.</Notice>
        ) : (
          <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(min(100%,220px),1fr))]">
            {availableGpus.map((p, i) => {
              const gt = vramByType.get(p.gpu_type);
              return (
                <div
                  key={`${p.gpu_type}:${p.region}:${p.tier}:${i}`}
                  className="ps-card p-4 flex flex-col gap-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <b className="font-semibold text-ps-text">{formatGpuName(p.gpu_type)}</b>
                    {p.available ? (
                      <Tag tone={p.instant_boot ? 'ok' : 'info'}>{p.instant_boot ? 'Instant' : 'Available'}</Tag>
                    ) : (
                      <Tag tone="off">Unavailable</Tag>
                    )}
                  </div>
                  <div className="text-xs text-ps-subtle">
                    {vramLabel(gt)} · {p.region} · {p.tier}
                  </div>
                  <div className="mt-auto flex items-center justify-between gap-2">
                    <span className="font-display text-xl text-ps-text">
                      {formatINR(p.price_per_hour)}
                      <span className="text-xs text-ps-subtle">/hr</span>
                    </span>
                    <button
                      type="button"
                      className="ps-btn2 ps-btn-sm"
                      disabled={!p.available}
                      onClick={() => launch(p)}
                    >
                      Rent
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </Page>
  );
}
